/**
 * Where an exported project gets the gotti packages while they are not on npm: GitHub, from the release tags
 * `npm run release:git` pushes to the gotti repository. Each released package is one orphan commit holding exactly what
 * `npm pack` ships, tagged `npm/<package slug>/<gotti2 commit>` — the scheme goon-riot's releases use, so the two
 * share tags. A tag is never moved, `npm install` needs nothing but git access to the repository, and the lockfile pins
 * each tag's commit.
 *
 * Kept free of any file or network access: the CLI's project generator bundles it.
 */

/** The GitHub repository the release tags live in. */
export const GOTTI_GITHUB_REPO = 'visgotti/gotti';

/** The gotti2 commit whose release an exported project installs. `npm run release:git` sets it. */
export const GOTTI_RELEASE_SHA = '1b65ff55a2a06bc03c6155726a6c9bb4ff8f9187';

/** The packages a project installs from the release — every gotti package it depends on. */
export const GOTTI_RELEASED_PACKAGES = [
  '@gotti/core',
  '@gotti/client',
  '@gotti/server',
  '@gotti/deployer',
  '@gotti/cli',
  '@gotti/playground',
  '@gotti/test-utils',
] as const;

/** A package's release tag: `@gotti/core` at `<sha>` is `npm/gotti-core/<sha>`. */
export const releaseTag = (name: string, sha: string = GOTTI_RELEASE_SHA): string =>
  `npm/${name.replace(/^@/, '').replace('/', '-')}/${sha}`;

/** The dependency spec that installs a released package: `github:visgotti/gotti#npm/gotti-core/<sha>`. */
export const releaseSpec = (name: string, sha: string = GOTTI_RELEASE_SHA): string =>
  `github:${GOTTI_GITHUB_REPO}#${releaseTag(name, sha)}`;

const RELEASED: ReadonlySet<string> = new Set(GOTTI_RELEASED_PACKAGES);
const SECTIONS = ['dependencies', 'devDependencies', 'optionalDependencies'] as const;

/**
 * `packageJson` (a project's package.json text) with every released gotti package it depends on pinned to the release —
 * a saved project's `file:../../packages/*` links included — and every other entry as it was, written back as the
 * scaffold writes it. Text that is not a JSON object comes back unchanged: it is the person's file.
 */
export function pinGottiRelease(packageJson: string, sha: string = GOTTI_RELEASE_SHA): string {
  let manifest: unknown;
  try {
    manifest = JSON.parse(packageJson);
  } catch {
    return packageJson;
  }
  if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) return packageJson;
  const pinned: Record<string, unknown> = { ...(manifest as Record<string, unknown>) };
  for (const section of SECTIONS) {
    const deps = pinned[section];
    if (!deps || typeof deps !== 'object' || Array.isArray(deps)) continue;
    pinned[section] = Object.fromEntries(
      Object.entries(deps as Record<string, unknown>).map(([name, spec]) => [name, RELEASED.has(name) ? releaseSpec(name, sha) : spec]),
    );
  }
  return `${JSON.stringify(pinned, null, 2)}\n`;
}
